import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import ReactPlayer from 'react-player/youtube';
import { PlayCircle } from 'lucide-react';

const TestVideoNoStrict = () => {
  const [playerReady, setPlayerReady] = useState(false);
  const [playerError, setPlayerError] = useState(null);
  const [consoleMessages, setConsoleMessages] = useState([]);

  // The specific video URL from the review request
  const videoUrl = "https://www.youtube.com/watch?v=dQw4w9WgXcQ";

  useEffect(() => {
    console.log('TestVideoNoStrict component mounted');
    
    // Capture console messages
    const originalConsoleLog = console.log;
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    console.log = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'log', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleLog(...args);
    };

    console.error = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'error', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleError(...args);
    };

    console.warn = (...args) => {
      setConsoleMessages(prev => [...prev, { type: 'warn', message: args.join(' '), timestamp: new Date().toISOString() }]);
      originalConsoleWarn(...args);
    };

    return () => {
      console.log = originalConsoleLog;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
    };
  }, []);

  const handlePlayerReady = () => {
    console.log('ReactPlayer is ready - NO STRICT MODE');
    setPlayerReady(true);
  };

  const handlePlayerError = (error) => {
    console.error('ReactPlayer error - NO STRICT MODE:', error);
    setPlayerError(error);
  };

  const handlePlayerStart = () => {
    console.log('ReactPlayer started playing - NO STRICT MODE');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">YouTube Video Test - NO STRICT MODE</h1>
        
        <div className="bg-red-50 p-6 rounded-lg mb-8 border border-red-200">
          <h2 className="text-xl font-semibold mb-4 text-red-800">⚠️ StrictMode Disabled</h2>
          <p className="text-red-700">This test page runs without React.StrictMode to check if that's causing the ReactPlayer issue.</p>
        </div>

        {/* Video Player Test */}
        <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
          <div className="flex items-center gap-2 mb-4">
            <PlayCircle className="w-5 h-5" style={{ color: '#102862' }} />
            <h2 className="text-xl font-semibold">TEST VIDEO - ReactPlayer (YouTube-specific import)</h2>
          </div>
          
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-4">
            <ReactPlayer
              url={videoUrl}
              width="100%"
              height="100%"
              controls
              muted={true}
              onReady={handlePlayerReady}
              onError={handlePlayerError}
              onStart={handlePlayerStart}
              config={{
                youtube: {
                  playerVars: {
                    showinfo: 1,
                    controls: 1,
                    modestbranding: 1,
                    rel: 0
                  }
                }
              }}
            />
          </div>

          {/* Player Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className={`p-3 rounded ${playerReady ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              <strong>Player Ready:</strong> {playerReady ? 'Yes' : 'No'}
            </div>
            <div className={`p-3 rounded ${playerError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
              <strong>Player Error:</strong> {playerError ? 'Yes' : 'No'}
            </div>
            <div className="p-3 rounded bg-blue-100 text-blue-800">
              <strong>Import:</strong> react-player/youtube
            </div>
          </div>

          {playerError && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
              <h3 className="font-semibold text-red-800 mb-2">Player Error Details:</h3>
              <pre className="text-sm text-red-700 whitespace-pre-wrap">{JSON.stringify(playerError, null, 2)}</pre>
            </div>
          )}
        </div>

        {/* Console Messages */}
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold mb-4">Console Messages</h2>
          <div className="max-h-96 overflow-y-auto">
            {consoleMessages.length === 0 ? (
              <p className="text-gray-500">No console messages yet...</p>
            ) : (
              <div className="space-y-2">
                {consoleMessages.map((msg, index) => (
                  <div key={index} className={`p-2 rounded text-sm ${
                    msg.type === 'error' ? 'bg-red-50 text-red-800' :
                    msg.type === 'warn' ? 'bg-yellow-50 text-yellow-800' :
                    'bg-gray-50 text-gray-800'
                  }`}>
                    <div className="flex justify-between items-start">
                      <span className="font-mono">[{msg.type.toUpperCase()}] {msg.message}</span>
                      <span className="text-xs opacity-60">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Render without StrictMode
const renderWithoutStrictMode = () => {
  const container = document.getElementById('test-video-container');
  if (container) {
    const root = ReactDOM.createRoot(container);
    root.render(<TestVideoNoStrict />);
  }
};

const TestVideoNoStrictWrapper = () => {
  useEffect(() => {
    // Create a container for the non-strict mode component
    const container = document.createElement('div');
    container.id = 'test-video-container';
    container.style.width = '100%';
    container.style.height = '100vh';
    
    // Replace the current content
    document.body.innerHTML = '';
    document.body.appendChild(container);
    
    // Render without StrictMode
    renderWithoutStrictMode();
  }, []);

  return <div>Loading test without StrictMode...</div>;
};

export default TestVideoNoStrictWrapper;